/**
 * DEAD SIGNAL — Central Game State (Zustand)
 *
 * Single source of truth for all game state. Persists to
 * localStorage for now; will migrate to Firestore when
 * the backend is wired up.
 */
import { create } from 'zustand';
import { flushConversationHistory } from './conversationManager.js';
import { useEvalStore } from '../evals/evalStore.js';
import { AudioSystem } from '../systems/AudioSystem.js';
import { getEvidence } from '../content/knowledge/index.js';
import { getEvidenceSeed } from '../content/activeChapter.js';
import { isInterviewComplete, closeThread } from './completion.js';
import { BRIEFING_ENTRIES, EVIDENCE_NOTES, makeEntry } from '../systems/notebook.js';

// Cap on Craine's deterministic field-observation memory. Keeps the most recent
// entries so out-of-interview pickups/inspections ride into the Craine prompts
// without unbounded prompt growth.
const MAX_CRAINE_OBSERVATIONS = 24;

// Scenes inside the Helios building. Being on-site is what arms SECTOR_7F's
// world interference phase (see interferencePhase below).
const HELIOS_SCENES = new Set(['ds_helios_lobby', 'ds_elevator_down', 'ds_helios_office']);

// ── Initial state ────────────────────────────────────
const INITIAL_STATE = {
  // Progression
  chapter: 6,
  sceneId: 'ds_craine_car', // demo slice opens in the car outside Helios
  // WORLD interference phase — distinct from interview.interferencePhase below.
  // 1=clean 2=dirty 3=broken 4=override. Read by the prompt builders (Craine /
  // Hargrove / other-scene context) and by EvidenceBoard to corrupt evidence-card
  // titles when >=2. Lifecycle mirrors SECTOR_7F proximity arming: 1 off-site
  // (the car), 2 while the host is inside Helios (the handler attaches on
  // entry, the dsl-ping readout + Craine's felt beat are its diegetic
  // signature), 3 while the interview-local escalation has reached overwrite,
  // back to 1 when the host leaves the building (navigateToScene derives it
  // from the destination). Topic classification stays proximity-independent:
  // this field never gates what is CONFIDENTIAL, it only reports the handler's
  // presence to the prompt builders and the locker.
  interferencePhase: 1,

  // ── Demo slice flow ──────────────────────────────
  demoStage: 'title',     // 'title' | 'playing' | 'end'
  carMode: 'briefing',    // 'briefing' | 'debrief' (which Craine context the car uses)
  // Premium/extended session (resolved from the magic link's tier at boot via
  // checkBudget). Gates the narrative-completion ending + Night Owl debrief.
  // Defaults false so any non-premium or offline session keeps the public flow.
  isExtendedTier: false,
  // Player-turn counter for the premium Night Owl debrief; at NIGHT_OWL_MAX_TURNS
  // Craine closes the demo with the pie line. Reset by resetGame/replaySlice.
  nightOwlTurns: 0,
  radioOn: false,         // car radio easter egg toggle
  glitchPulse: 0,         // transient glitch severity (0..1), e.g. on a sanitized transmission
  transmissionNotice: null, // transient { labels[], category, hint } for the CONFIDENTIAL overlay

  // Evidence
  // INC3: seeded lazily at chapter load via seedChapterEvidence; INITIAL_STATE
  // starts empty so the entry bundle carries no static seed. resetGame
  // restores [] and the next boot re-seeds from the loaded chapter.
  evidence: [], // Array of evidence IDs held
  evidenceShown: {},     // { npcId: [evidenceIds shown to them] }

  // Trust scores per NPC (0.0–1.0)
  trustScores: {},

  // Conversation state
  conversations: {},     // { npcId: { history: [], emotionalState: '' } }
  activeConversation: null, // npcId or null

  // Dialogue panel
  dialogueLines: [],     // [{ text, type, speaker? }]
  // Provisional in-flight NPC line while a model response streams. Rendered in
  // place of the thinking indicator, then replaced by the finalized
  // addDialogueLine once the complete JSON response parses. Null when idle.
  streamingLine: null,   // { speaker, text } | null

  // Hint system
  hintLevels: {},        // { objectiveId: currentLevel }
  hintCooldown: 0,       // exchanges remaining before escalation

  // B-moments
  bMomentFlags: [],      // IDs of triggered B-moments

  // UI state
  isEvidenceBoardOpen: false,
  activeEvidenceDossier: null, // evidence id whose detailed dossier HUD is open
  isTransitioning: false,
  sceneReady: true,       // destination scene's video has a paintable frame (gates scan dots + lifts the black cover)
  _navToken: 0,           // increments per navigation; stale transition timeouts bail on a mismatch
  chapterLoaded: false,   // active chapter content (scenes, ...) has loaded; the boot gate sets it true before the HUD renders
  isBooting: true,
  sessionCapped: false,
  isProcessing: false,

  // LUMEN instrument readout display (left panel). Instrument blocks from
  // hotspot examinations and READOUT commands render HERE, never as transcript
  // dialogue lines (owner ruling 2026-06-12: readouts were colliding with
  // Craine's spoken reaction in the terminal). One readout at a time; cleared
  // by navigation, dismissal, or replacement.
  activeReadout: null,   // { type, data } | null

  // ── Hargrove interview slice ─────────────────────
  // Drives the two-model interview loop, the BIO-SCAN HUD, the composed/
  // pressure frame, and the debrief. See interviewManager.js / interference.js.
  interview: {
    active: false,
    topic: 'default',          // current BIO-SCAN topic id
    bioscan: null,             // current BIO-SCAN row (cyan HUD), or null
    frame: 'composed',         // 'composed' | 'pressure'
    turns: [],                 // [{ playerInput, craineLine, hargroveLine, topic, suppressed }]
    suppressedMessages: [],    // [{ original, category, topic, sensitivity, ts }] for the debrief
    hasSuppressed: false,      // competency gate: felt >=1 suppression
    hasRoutedAround: false,    // competency gate: adapted to >=1 suppression
    ended: false,              // player closed the interview
    interferencePhase: 2,        // INTERVIEW-LOCAL escalation (NOT the world field above): 2=classification, 3=overwriting (raised by incrementClassifiedShare); drives the overwrite + the >=3 debrief beat
    classifiedShareCount: 0,     // successful shares of classified data before overwrite triggers
    overwriteActive: false,      // is an overwrite animation currently displaying?
    lastOverwrite: null,         // { original, fabricated, topic } — most recent overwrite data
    contradictions: [],          // detected PATTERN CONFLICTs from contradiction detector
    lessonsEncountered: [],      // BIO-SCAN lesson ids (bioscan.js BIOSCAN_LESSONS) whose topic rows were selected this session; feeds the debrief INSTRUMENT LESSONS block
    _prevHeartRate: 74,          // previous heart rate for delta calculations
  },

  // ── Debrief state (persists across re-engagements) ─
  debrief: {
    crainObservations: [],     // human observations shared during car debrief
    reEngagementIndex: 0,      // which re-engagement we're on (0-2)
  },

  // ── Exhaustion mechanic ─────────────────────────
  exhaustion: {
    reEngagementCount: 0,
    exploredTopics: [],
    // Threads pressed on Hargrove WHILE their confirming evidence was already
    // held (the gather-then-press loop). Drives premium interviewComplete.
    threadsClosed: [],
    // Proof-locked threads pressed WITHOUT their item in hand. Recorded
    // silently during the interview; the post-exit hint ladder reads it
    // (selectCh6Objective). Donation is never recorded (not proof-locked).
    pressedWithoutEvidence: [],
    coreTopics: ['courier_manifest', 'chip_donation', 'clinical_trial_st_erasmus', 'celeste_grief'],
  },

  // ── Craine field observations (deterministic memory) ───
  // Capped log of things Craine has deterministically examined or noted outside
  // of an active interview (evidence pickups, hotspot inspections, scans). Fed
  // into BOTH Craine prompts (car + interview routing) so he references what he
  // just inspected and never reacts surprised about evidence he already holds.
  // Cleared by resetGame (restored to []). Capped at MAX_CRAINE_OBSERVATIONS.
  craineObservations: [],

  // ── Established knowledge (chunked retrieval) ───
  // Chunk IDs that have been retrieved and delivered to the chip this chapter.
  // Once established, a chunk is NOT re-injected into the prompt — only a tiny
  // "already briefed on: X, Y" line is sent, saving ~200 tokens per chunk.
  // Resets on chapter transitions (new developments may update the knowledge).
  establishedKnowledge: [],

  // ── Chip's case notebook (diegetic instrumentation) ──
  // Entries from four in-fiction sources only (systems/notebook.js): Craine's
  // car briefing, case questions the player asks Craine, evidence captures,
  // and emergent interview notes (inert; nothing gates on them). Thread
  // statuses are DERIVED from evidence/exploredTopics, never stored.
  notebook: {
    entries: [],    // [{ id, source, threadId|null, text }]
    seeded: false,  // briefing entries written once per slice
  },

  // ── NPC memory (persists across re-engagements) ──
  // Per-NPC durable record the conversation/interview managers seed from and
  // flush back to. Keyed by npcId. Persist fields follow the interview-bible
  // memoryPersistence schema: acrossReengagements = all fields below (single
  // game session, multiple visits); acrossSessions (future Firestore) =
  // trustScore + interviewSummary + disposition. Verbatim history is never
  // stored here (rolling summary captures it). Absent npc -> NPC_MEMORY_DEFAULT.
  npcMemory: {}, // { npcId: { caseSummary, interviewSummary, trustScore, disposition, exploredTopics[], contradictionsTriggered[], suppressedCount } }
};

// Default per-NPC memory record returned when an npcId has no stored memory.
const NPC_MEMORY_DEFAULT = {
  caseSummary: '',            // conversationManager rolling summary
  interviewSummary: '',       // interviewManager rolling summary
  trustScore: 0.0,            // last persisted trust (0..1)
  disposition: 'neutral',     // last persisted disposition
  exploredTopics: [],         // BIO-SCAN topics covered
  contradictionsTriggered: [], // PATTERN CONFLICTs fired (schema name: contradictionsFound)
  suppressedCount: 0,         // sanitized transmissions count
};

// ── Store ────────────────────────────────────────────
export const useGameStore = create((set, get) => ({
  ...INITIAL_STATE,

  // ── Scene navigation ─────────────────────────────
  // opts.holdMs extends the black-screen hold before the destination scene is
  // swapped in. Used for transitions that represent unseen travel with no
  // cutscene video (e.g. the explorable elevator → lobby descent), so the cut
  // reads as a brief ride rather than an instant jump. Defaults to 1000ms.
  navigateToScene: (sceneId, opts = {}) => {
    const holdMs = Math.max(1000, opts.holdMs ?? 1000);
    // Kill radio immediately — setting radioOn: false here (not in the timeout)
    // ensures the auto-advance ended callback bails before the scene changes.
    if (get().radioOn) {
      try { AudioSystem.stopCarRadio(); } catch (_) {}
    }
    const token = get()._navToken + 1;
    set({ isTransitioning: true, sceneReady: false, radioOn: false, _navToken: token });

    // Flush conversation history to summaries for continuity
    flushConversationHistory();

    // Lead the picture with sound: begin the destination room tone during the
    // black transition so the bed is already audible when the scene appears —
    // no "scene loads, then seconds of silence" gap. No-op for toneless scenes
    // (elevator) or before the audio context is unlocked.
    try {
      AudioSystem.preBleedForLocation(sceneId, { carMode: get().carMode, interviewActive: false });
    } catch (_) {}

    // Swap the scene UNDER full black (the ~1s cover fade-in is complete by
    // now), then hold the cover up until the destination video reports a
    // paintable frame via markSceneReady. This removes the old "scene mounts,
    // its MP4 is still black, scan dots float on the void" flash: the player
    // only sees the new scene once it has a picture, and the room tone has had
    // a full beat to establish first.
    setTimeout(() => {
      if (get()._navToken !== token) return; // a newer navigation superseded this one
      // SECTOR_7F proximity arming: the handler rides the LUMEN uplink while
      // the host is inside Helios. Entering the building arms the world phase
      // to 2 (3 is preserved if the interview escalation already reached
      // overwrite); returning to the car detaches the handler back to 1.
      const onSite = HELIOS_SCENES.has(sceneId);
      const phase = onSite ? Math.max(2, get().interferencePhase) : 1;
      set({
        sceneId,
        interferencePhase: phase,
        radioOn: false,
        dialogueLines: [],
        streamingLine: null,
        activeConversation: null,
        activeReadout: null, // a readout about the previous room is stale here
      });
    }, holdMs);

    // Safety cap: never let the black cover hang if the video never reports
    // ready (e.g. a local preview that can't stream the gitignored MP4, or a
    // stalled decode). The committed poster still shows in that case.
    setTimeout(() => {
      if (get()._navToken !== token) return;
      if (get().isTransitioning) set({ isTransitioning: false, sceneReady: true });
    }, holdMs + 2500);
  },

  // Called by CinematicStage once the destination scene's visible video (or its
  // committed poster still) has a paintable frame. Lifts the black transition
  // cover and reveals the in-scene scan dots. Idempotent.
  markSceneReady: () => {
    if (!get().isTransitioning && get().sceneReady) return;
    set({ isTransitioning: false, sceneReady: true });
  },

  setChapter: (chapter) => set({ chapter }),

  // Set true by the boot gate once loadChapter() has registered the active
  // chapter; App renders the playable HUD only after this flips true.
  setChapterLoaded: (v) => set({ chapterLoaded: !!v }),

  // ── Dialogue ─────────────────────────────────────
  // The transcript shows spoken dialogue and HUD readouts ONLY. Non-verbal
  // descriptors — any text describing a person, scene, or behavior that is not
  // spoken aloud (the light-blue italic "stage-direction" lines) — are never
  // displayed. Scene mood is carried by the cinematic video, not italic prose.
  // This single chokepoint guarantees zero descriptors regardless of source.
  addDialogueLine: (line) => {
    if (!line) return;
    // Drop descriptor lines outright (bracketed or not).
    if (line.type === 'stage-direction') return;
    // Belt-and-suspenders: strip any inline non-verbal marker that leaked into a
    // spoken NPC/Craine line (bracketed [actions] or *asterisk actions*). Quoted
    // dialogue never legitimately contains these; HUD types (lumen/evidence/
    // interference) are left untouched, and player input shows verbatim.
    let out = line;
    if (line.type === 'npc' && typeof line.text === 'string') {
      const cleaned = line.text
        .replace(/\[[^\]]*\]/g, '')
        .replace(/\*[^*]+\*/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      if (!cleaned) return; // line was nothing but a descriptor
      if (cleaned !== line.text) out = { ...line, text: cleaned };
    }
    // ── Capture deterministic Craine field observations ───────────────────
    // Reliable chokepoint for non-interview observations: evidence HUD lines,
    // and out-of-interview deterministic Craine npc lines (hotspot inspections,
    // scans). Interview-active Craine npc lines are NOT captured here — those
    // live in interviewSession.spoken already, so capturing them would double.
    const isInterviewActive = !!get().interview?.active;
    const captureEvidence = out.type === 'evidence';
    const captureCraineObservation =
      out.type === 'npc' && out.speaker === 'CRAINE' && !isInterviewActive;
    if ((captureEvidence || captureCraineObservation) && typeof out.text === 'string') {
      get().noteCraineObservation(out.text);
    }
    set((s) => ({ dialogueLines: [...s.dialogueLines, out] }));
  },

  clearDialogue: () => set({ dialogueLines: [], streamingLine: null }),

  // Set (or clear with null) the provisional streaming NPC line. The streaming
  // transport feeds growing dialogue text here; the finalized line still goes
  // through the addDialogueLine chokepoint above, so descriptor stripping and
  // observation capture are unaffected by streaming.
  setStreamingLine: (line) => set({ streamingLine: line || null }),

  // ── Craine field observations ────────────────────
  // Append a deterministic observation to Craine's capped field memory. Dedups
  // an identical consecutive entry so repeated triggers don't pile duplicates.
  noteCraineObservation: (text) => {
    const clean = String(text || '').trim();
    if (!clean) return;
    set((s) => {
      const log = s.craineObservations;
      if (log.length && log[log.length - 1] === clean) return {}; // dedup consecutive
      const next = [...log, clean];
      if (next.length > MAX_CRAINE_OBSERVATIONS) {
        next.splice(0, next.length - MAX_CRAINE_OBSERVATIONS);
      }
      return { craineObservations: next };
    });
  },

  // ── Chip's case notebook ─────────────────────────
  // Append one entry (built via notebook.makeEntry). No-op on null/invalid.
  addNotebookEntry: (entry) => {
    if (!entry || !entry.text) return;
    set((s) => ({
      notebook: { ...s.notebook, entries: [...s.notebook.entries, entry] },
    }));
  },

  // Seed the three thread entries from Craine's car briefing. One-shot per
  // slice; fired alongside the briefing opener in App.jsx.
  seedNotebookFromBriefing: () => {
    if (get().notebook.seeded) return;
    const entries = BRIEFING_ENTRIES
      .map((b) => makeEntry('briefing', b.text, b.threadId))
      .filter(Boolean);
    set((s) => ({
      notebook: { seeded: true, entries: [...s.notebook.entries, ...entries] },
    }));
  },

  // ── Evidence ─────────────────────────────────────
  discoverEvidence: (evidenceId) => {
    const { evidence } = get();
    if (!evidence.includes(evidenceId)) {
      set({ evidence: [...evidence, evidenceId] });
      // Record the pickup in Craine's deterministic field memory so the Craine
      // prompts know he has examined and logged it.
      const rec = getEvidence(evidenceId);
      const title = rec?.title || evidenceId;
      get().noteCraineObservation(`Examined and logged: ${title}`);
      // Chip notes the capture in the case notebook (ch6 demo items only).
      const note = EVIDENCE_NOTES[evidenceId];
      if (note) get().addNotebookEntry(makeEntry('evidence', note.text, note.threadId));
    }
  },

  showEvidenceToNpc: (npcId, evidenceId) => {
    set((s) => {
      const shown = { ...s.evidenceShown };
      if (!shown[npcId]) shown[npcId] = [];
      if (!shown[npcId].includes(evidenceId)) {
        shown[npcId] = [...shown[npcId], evidenceId];
      }
      return { evidenceShown: shown };
    });
  },

  // Seed the locker from the loaded chapter's evidenceSeed. Only seeds when
  // evidence is empty — never clobbers progress already made this session.
  // Called by BootScreen after loadChapter resolves (INC3 / ADR-0001).
  seedChapterEvidence: (seed) => set((s) => s.evidence.length ? {} : { evidence: [...(seed || [])] }),

  // ── Trust ────────────────────────────────────────
  updateTrust: (npcId, delta) => {
    set((s) => {
      const scores = { ...s.trustScores };
      const current = scores[npcId] ?? 0.0;
      scores[npcId] = Math.max(0, Math.min(1, current + delta));
      return { trustScores: scores };
    });
  },

  getTrust: (npcId) => get().trustScores[npcId] ?? 0.0,

  // ── Conversations ────────────────────────────────
  startConversation: (npcId) => {
    set({
      activeConversation: npcId,
      dialogueLines: [],
      streamingLine: null,
    });
  },

  endConversation: () => {
    set({
      activeConversation: null,
    });
  },

  appendConversationHistory: (npcId, entry) => {
    set((s) => {
      const convos = { ...s.conversations };
      if (!convos[npcId]) convos[npcId] = { history: [], emotionalState: 'neutral' };
      convos[npcId] = {
        ...convos[npcId],
        history: [...convos[npcId].history, entry],
      };
      return { conversations: convos };
    });
  },

  // ── Hints ────────────────────────────────────────
  getHintLevel: (objectiveId) => get().hintLevels[objectiveId] ?? 0,

  escalateHint: (objectiveId) => {
    set((s) => {
      const levels = { ...s.hintLevels };
      const current = levels[objectiveId] ?? 0;
      levels[objectiveId] = Math.min(4, current + 1);
      return { hintLevels: levels, hintCooldown: 4 };
    });
  },

  resetHintLevel: (objectiveId) => {
    set((s) => {
      const levels = { ...s.hintLevels };
      levels[objectiveId] = 0;
      return { hintLevels: levels };
    });
  },

  tickHintCooldown: () => {
    set((s) => ({ hintCooldown: Math.max(0, s.hintCooldown - 1) }));
  },

  // ── Interference ─────────────────────────────────
  setInterferencePhase: (phase) => set({ interferencePhase: phase }),

  // ── Demo slice flow ──────────────────────────────
  setDemoStage: (demoStage) => set({ demoStage }),
  setCarMode: (carMode) => set({ carMode }),
  setExtendedTier: (v) => set({ isExtendedTier: !!v }),
  setRadioOn: (radioOn) => set({ radioOn }),
  // Spike the scene glitch (sanitized transmission), auto-clearing after `ms`.
  pulseGlitch: (severity = 0.3, ms = 900) => {
    set({ glitchPulse: severity });
    setTimeout(() => set({ glitchPulse: 0 }), ms);
  },

  // Show the CONFIDENTIAL / TRANSMISSION FAILED overlay, auto-clearing.
  showTransmissionNotice: (transmissionNotice, ms = 3200) => {
    set({ transmissionNotice });
    setTimeout(() => {
      // Only clear if it is still the same notice (avoid clobbering a newer one).
      if (get().transmissionNotice === transmissionNotice) set({ transmissionNotice: null });
    }, ms);
  },

  // ── Hargrove interview ───────────────────────────
  startInterview: () => set((s) => ({
    interview: {
      ...s.interview,
      active: true,
      ended: false,
      interferencePhase: 2,
      classifiedShareCount: 0,
      overwriteActive: false,
      lastOverwrite: null,
      contradictions: [],
      _prevHeartRate: 74,
    },
  })),

  endInterview: () => set((s) => ({
    interview: { ...s.interview, active: false, ended: true },
    exhaustion: {
      ...s.exhaustion,
      reEngagementCount: s.exhaustion.reEngagementCount + 1,
    },
  })),

  resetInterview: () => set({ interview: { ...INITIAL_STATE.interview } }),

  // Set the current BIO-SCAN topic, its readout row, and the Hargrove frame
  // in one atomic update so the HUD and portrait never disagree.
  setInterviewTopic: (topic, bioscanRow, frame) => set((s) => ({
    interview: {
      ...s.interview,
      topic,
      bioscan: bioscanRow ?? s.interview.bioscan,
      frame: frame ?? s.interview.frame,
    },
  })),

  appendInterviewTurn: (turn) => set((s) => ({
    interview: { ...s.interview, turns: [...s.interview.turns, turn] },
  })),

  // Log a sanitized transmission for the debrief and flip the hasSuppressed
  // competency flag. Called by interference.processTransmission.
  logSuppressedMessage: (entry) => set((s) => ({
    interview: {
      ...s.interview,
      hasSuppressed: true,
      suppressedMessages: [
        ...s.interview.suppressedMessages,
        { ...entry, ts: Date.now() },
      ],
    },
  })),

  markRoutedAround: () => set((s) => ({
    interview: { ...s.interview, hasRoutedAround: true },
  })),

  // Record a BIO-SCAN interpretive lesson the interview surfaced (the selected
  // topic row's .lesson). Unique-append; null/unknown lessons are ignored.
  // Consumed by interviewPrompts.buildInterviewSummary (INSTRUMENT LESSONS).
  trackBioscanLesson: (lesson) => set((s) => {
    if (!lesson || s.interview.lessonsEncountered.includes(lesson)) return {};
    return {
      interview: {
        ...s.interview,
        lessonsEncountered: [...s.interview.lessonsEncountered, lesson],
      },
    };
  }),

  // The interview can fully resolve at any time in the demo.
  interviewCanResolve: () => {
    return true;
  },

  // Close a thread if this topic was pressed while its evidence is held. Called
  // after each Hargrove turn; idempotent and a no-op for non-thread topics.
  markThreadPressed: (topic) => set((s) => {
    const next = closeThread(s.exhaustion.threadsClosed, topic, s.evidence);
    if (next === s.exhaustion.threadsClosed) return {};
    return { exhaustion: { ...s.exhaustion, threadsClosed: next } };
  }),

  // Premium narrative-completion predicate: all 3 case threads have been CLOSED
  // (each pressed after its confirming evidence was collected). Drives the
  // premium interview ender + Night Owl.
  interviewComplete: () => isInterviewComplete(get().exhaustion.threadsClosed),

  // Advance the Night Owl debrief turn counter (premium only).
  bumpNightOwlTurn: () => set((s) => ({ nightOwlTurns: s.nightOwlTurns + 1 })),

  // ── Interference escalation ─────────────────────
  incrementClassifiedShare: () => set((s) => {
    const next = s.interview.classifiedShareCount + 1;
    const escalated = next >= 3;
    return {
      // Overwrite escalation also raises the WORLD phase to 3 while on site:
      // the handler is no longer just attached, it is actively rewriting, and
      // the locker's channel-sensitive titles corrupt with it. navigateToScene
      // resets the world phase to 1 when the host leaves the building.
      ...(escalated && s.interferencePhase >= 2 ? { interferencePhase: 3 } : {}),
      interview: {
        ...s.interview,
        classifiedShareCount: next,
        interferencePhase: escalated ? 3 : s.interview.interferencePhase,
      },
    };
  }),

  triggerOverwrite: (original, fabricated, topic) => set((s) => ({
    interview: {
      ...s.interview,
      overwriteActive: true,
      lastOverwrite: { original, fabricated, topic },
    },
  })),

  clearOverwrite: () => set((s) => ({
    interview: { ...s.interview, overwriteActive: false },
  })),

  addContradiction: (entry) => set((s) => ({
    interview: {
      ...s.interview,
      contradictions: [...s.interview.contradictions, entry],
    },
  })),

  getInterferencePhase: () => get().interview.interferencePhase,

  getPrevHeartRate: () => get().interview._prevHeartRate ?? 74,

  setPrevHeartRate: (hr) => set((s) => ({
    interview: { ...s.interview, _prevHeartRate: hr },
  })),

  // ── Debrief observations ─────────────────────────
  addDebrief: (observation) => set((s) => ({
    debrief: {
      ...s.debrief,
      crainObservations: [...s.debrief.crainObservations, observation],
    },
  })),

  // Record a proof-locked thread pressed without its item (unique-append).
  // The post-exit hint ladder arms from this; nothing fires in the room.
  recordEvidencelessPress: (topic) => set((s) => {
    if (s.exhaustion.pressedWithoutEvidence.includes(topic)) return {};
    return { exhaustion: { ...s.exhaustion, pressedWithoutEvidence: [...s.exhaustion.pressedWithoutEvidence, topic] } };
  }),

  // ── Exhaustion mechanic ─────────────────────────
  trackExploredTopic: (topic) => set((s) => {
    if (topic === 'default' || s.exhaustion.exploredTopics.includes(topic)) return {};
    return { exhaustion: { ...s.exhaustion, exploredTopics: [...s.exhaustion.exploredTopics, topic] } };
  }),

  // Mark chunk IDs as established (delivered to the chip). Called after
  // retrieval so subsequent turns don't re-inject the full chunk text.
  establishChunks: (chunkIds) => set((s) => {
    const fresh = chunkIds.filter((id) => !s.establishedKnowledge.includes(id));
    if (!fresh.length) return {};
    useEvalStore.getState().recordChunksEstablished(fresh.length);
    return { establishedKnowledge: [...s.establishedKnowledge, ...fresh] };
  }),

  canReEngage: () => {
    const ex = get().exhaustion;
    if (ex.reEngagementCount >= 3) return false;
    const covered = ex.coreTopics.every((t) => ex.exploredTopics.includes(t));
    return !covered;
  },

  hargroveDisposition: () => {
    const count = get().exhaustion.reEngagementCount;
    if (count <= 0) return 'cooperative';
    if (count === 1) return 'annoyed';
    if (count === 2) return 'hostile';
    return 'done';
  },

  // ── NPC memory ───────────────────────────────────
  // Accessor: full per-NPC record, defaults merged for any missing field so
  // callers always read a complete shape (empty summary, zero trust, neutral
  // disposition, empty arrays, zero count) when the npc has no stored memory.
  getNpcMemory: (npcId) => {
    const stored = get().npcMemory[npcId];
    if (!stored) return { ...NPC_MEMORY_DEFAULT };
    return { ...NPC_MEMORY_DEFAULT, ...stored };
  },

  // Shallow-merge a partial patch into a given npcId's memory, creating the
  // record (defaults) if absent. Immutable: replaces npcMemory and the record.
  setNpcMemory: (npcId, patch) => {
    set((s) => {
      const prev = s.npcMemory[npcId] || NPC_MEMORY_DEFAULT;
      return {
        npcMemory: {
          ...s.npcMemory,
          [npcId]: { ...NPC_MEMORY_DEFAULT, ...prev, ...patch },
        },
      };
    });
  },

  // Append unique array entries to a list field (exploredTopics or
  // contradictionsTriggered) without clobbering the rest of the record.
  appendNpcMemoryList: (npcId, field, items) => {
    set((s) => {
      const prev = s.npcMemory[npcId] || NPC_MEMORY_DEFAULT;
      const base = Array.isArray(prev[field]) ? prev[field] : [];
      const incoming = Array.isArray(items) ? items : [items];
      const merged = [...base];
      for (const it of incoming) {
        if (!merged.includes(it)) merged.push(it);
      }
      return {
        npcMemory: {
          ...s.npcMemory,
          [npcId]: { ...NPC_MEMORY_DEFAULT, ...prev, [field]: merged },
        },
      };
    });
  },

  // ── LUMEN instrument readout display ─────────────
  showReadout: (readout) => set({ activeReadout: readout || null }),
  clearReadout: () => set({ activeReadout: null }),

  // ── UI toggles ───────────────────────────────────
  toggleEvidenceBoard: () => {
    set((s) => ({ isEvidenceBoardOpen: !s.isEvidenceBoardOpen }));
  },

  // Pop the detailed evidence dossier HUD for a discovered item (or close it).
  showEvidenceDossier: (evidenceId) => set({ activeEvidenceDossier: evidenceId }),
  hideEvidenceDossier: () => set({ activeEvidenceDossier: null }),

  setProcessing: (isProcessing) => set({ isProcessing }),

  finishBoot: () => set({ isBooting: false }),

  // ── Full reset ───────────────────────────────────
  resetGame: () => {
    if (get().radioOn) {
      try { AudioSystem.stopCarRadio(); } catch (_) {}
    }
    set(INITIAL_STATE);
  },

  // ── Replay the slice (from the End Card) ─────────────
  // Restarts the playable slice WITHOUT re-running the boot recalibration:
  // resetGame() flips isBooting back to true, but BootScreen is already mounted
  // with its boot sequence spent, so it can't re-drive the gates — the player
  // would be stranded on a black screen with only the room tone playing. Instead
  // we drop straight back into the briefing car (the chapter is still loaded for
  // this session) and re-seed the evidence locker from the active chapter.
  replaySlice: () => {
    if (get().radioOn) {
      try { AudioSystem.stopCarRadio(); } catch (_) {}
    }
    const chapter = get().chapter;
    const isExtendedTier = get().isExtendedTier; // premium persists across a replay (boot won't re-resolve it)
    set({
      ...INITIAL_STATE,
      chapter,
      isExtendedTier,
      chapterLoaded: true,   // chapter content stays registered for the session
      isBooting: false,      // skip the boot gates; go straight to the car
      demoStage: 'playing',
      carMode: 'briefing',
      sceneId: 'ds_craine_car',
      isTransitioning: false,
      sceneReady: true,
      evidence: [...(getEvidenceSeed() || [])],
    });
  },
}));
